import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Observable, Subject, of } from 'rxjs';
import { map, catchError, shareReplay, tap, finalize } from 'rxjs/operators';
import { CartItem, Order, OrderStatus } from '../models';
import { StoreOrderDetails } from '../models/store-order-details';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private readonly sessionKey = 'hypenationSessionId';
  private readonly orderKey = 'hypenationCreatedOrder';
  private inFlightSession$: Observable<string | null> | null = null;

  /**
   * Memoized result of the pending-order boot guard. Stays populated for the
   * lifetime of the app unless a new order is saved (see `saveOrderDetails`),
   * in which case the guard re-runs on the next verifyPendingOrder() call.
   */
  private pendingOrderGuard$: Observable<void> | null = null;

  /**
   * Emits whenever the guard (or any other flow) rotates the session.
   * CartStateService subscribes to this so the cached cart is dropped in
   * lockstep with `clearSessionId()`, without SessionService needing a
   * direct dependency on CartStateService.
   */
  private readonly sessionRotationSubject = new Subject<void>();
  public readonly sessionRotation$ = this.sessionRotationSubject.asObservable();

  constructor(private readonly _apiService: ApiService) {}

  saveSessionId(sessionId: string): void {
    localStorage.setItem(this.sessionKey, sessionId);
  }


  getSessionId(): Observable<string | null> {
    const sessionId = localStorage.getItem(this.sessionKey);

    if (sessionId) {
      return of(sessionId);
    }

    if (this.inFlightSession$) {
      return this.inFlightSession$;
    }

    this.inFlightSession$ = this._apiService.getCurrentSession().pipe(
      map((res) => {
        if (res.body && res.body.uid) {
          const newSession = res.body.uid.toString();
          this.saveSessionId(newSession);
          return newSession;
        }
        return null;
      }),
      catchError((error) => {
        console.error('Error fetching session ID:', error);
        return of(null);
      }),
      finalize(() => {
        // Allow re-fetch only if nothing was stored (e.g. on error).
        if (!localStorage.getItem(this.sessionKey)) {
          this.inFlightSession$ = null;
        }
      }),
      shareReplay(1)
    );

    return this.inFlightSession$;
  }


  // saveOrder(order: Order): void {
  //   localStorage.setItem(this.orderKey, JSON.stringify(order));
  // }

  // getOrder(): null | Order {
  //   const order = localStorage.getItem(this.orderKey);
  //   if(order){
  //     return JSON.parse(order)
  //   }
  //   return null
  // }


  saveOrderDetails(order: Order, cartItems : CartItem[]): void {

    const orderDetails  : StoreOrderDetails = {
      order : order,
      orderItems : cartItems
    }
    localStorage.setItem(this.orderKey, JSON.stringify(orderDetails));
    // A new pending order invalidates the previous guard verification —
    // otherwise we'd replay a stale "already verified" signal and skip
    // checking this order's status on subsequent cart resolves.
    this.pendingOrderGuard$ = null;
  }

  getOrderDetails(): null | StoreOrderDetails {
    const order = localStorage.getItem(this.orderKey);
    if(order){
      return JSON.parse(order)
    }
    return null
  }

  /**
   * Returns the order number of an in-flight order (one that has been
   * created via /checkout but is not yet completed/cancelled), or null
   * if there is none. Used to redirect navbar cart clicks back to the
   * checkout page so the user can resume payment instead of seeing the
   * now-empty cart.
   */
  getPendingOrderNumber(): string | null {
    const details = this.getOrderDetails();
    if (!details?.order) return null;

    const status = details.order.status;
    const isPending =
      status === OrderStatus.INITIATED ||
      status === OrderStatus.RESERVED ||
      // Some backends return no status until polling — treat that as pending too.
      status === undefined ||
      status === null;

    return isPending ? details.order.orderNumber : null;
  }

  /**
   * Drop the locally stored order details. Call this when the order
   * reaches a terminal state (COMPLETED / CANCELLED / CLOSED) so the
   * navbar stops trying to resume it.
   */
  clearPendingOrder(): void {
    localStorage.removeItem(this.orderKey);
  }


  clearSessionId(): void {
    localStorage.removeItem(this.sessionKey);
    this.inFlightSession$ = null;
  }

  /**
   * Rotate the session: drop the pending-order pointer, notify cart-state
   * subscribers so the cached cart is cleared, then drop the session ID.
   * The `sessionRotation$` emission runs synchronously between the two so
   * CartStateService sees the rotation before the session ID is gone.
   *
   * Public because there are two legitimate callers:
   *   1. The pending-order boot guard below (`verifyPendingOrder`), which
   *      rotates when a cached order pointer is terminal or unreachable.
   *   2. `CartStateService` stale-cart recovery, which rotates when a cart
   *      mutation fails with a backend not-found (`RecordNotFoundException`)
   *      because the cached cart UID no longer exists.
   * Both rotate only on a positive poison signal — do not call this
   * speculatively from new code paths.
   */
  public rotateSession(): void {
    this.clearPendingOrder();
    this.sessionRotationSubject.next();
    this.clearSessionId();
  }

  /**
   * Pending-order boot guard. If a `hypenationCreatedOrder` pointer is
   * cached, verify its status against the server before any cart resolve
   * is served. When the server reports a terminal status — or the request
   * fails — rotate the session so the next `resolveCart()` starts fresh.
   *
   * The returned Observable is memoized via `shareReplay(1)`, so concurrent
   * callers share a single HTTP round-trip and subsequent callers short-
   * circuit to the cached completion. Writing a new pending order via
   * `saveOrderDetails()` invalidates the memoization.
   */
  public verifyPendingOrder(): Observable<void> {
    if (this.pendingOrderGuard$) {
      return this.pendingOrderGuard$;
    }

    const orderDetails = this.getOrderDetails();
    const orderNumber = orderDetails?.order?.orderNumber;
    if (!orderNumber) {
      this.pendingOrderGuard$ = of(void 0).pipe(shareReplay(1));
      return this.pendingOrderGuard$;
    }

    this.pendingOrderGuard$ = this._apiService
      .getOrdersByOrderNumber(orderNumber)
      .pipe(
        tap((res) => {
          const status = res.body?.status;
          const isTerminal =
            status === OrderStatus.COMPLETED ||
            status === OrderStatus.CANCELLED ||
            status === OrderStatus.CLOSED;
          if (isTerminal) {
            this.rotateSession();
          }
        }),
        map(() => void 0),
        catchError((err) => {
          // Fail-open rotation: starting fresh is safer than honouring
          // state we can't verify.
          console.warn(
            'Pending-order guard verification failed; rotating session',
            err
          );
          this.rotateSession();
          return of(void 0);
        }),
        shareReplay(1)
      );

    return this.pendingOrderGuard$;
  }
}
